import React, { useState, useEffect } from 'react';
import { useTitle } from '../../../context/TitleContext';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaStethoscope, FaSearch, FaUserMd, FaPhone, FaToggleOn, FaToggleOff, FaBan, FaCheck, FaCalendarAlt, FaPlus } from 'react-icons/fa';
import { showSuccessToast, showErrorToast } from '../../../shared/utils/toastHelper';
import EnhancedPagination from '../../../shared/components/EnhancedPagination';
import apiClient from '../../../shared/utils/apiConfig';
import { usePermissions } from '../../../shared/hooks/usePermissions';
import axios from 'axios';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const AdminDoctors = () => {
    const { setTitle } = useTitle();
    const navigate = useNavigate();
    const { hasPermission, isSuperAdmin } = usePermissions();
    
    const [doctors, setDoctors] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('active');
    const [currentPage, setCurrentPage] = useState(1);
    const [pagination, setPagination] = useState(null);
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [addForm, setAddForm] = useState({
        name: '',
        phone: '',
        password: '',
        description_ar: '',
        description_en: '',
        subcategory_id: '',
        sub_sub_category_id: '',
        gender: ''
    });
    const [addingDoctor, setAddingDoctor] = useState(false);
    const [subCategories, setSubCategories] = useState([]);
    const [subSubCategories, setSubSubCategories] = useState([]);
    const [loadingSubCategories, setLoadingSubCategories] = useState(false);
    const [loadingSubSubCategories, setLoadingSubSubCategories] = useState(false);

    useEffect(() => {
        setTitle('إدارة الأطباء');
    }, [setTitle]);

    useEffect(() => {
        fetchDoctors();
    }, [currentPage, statusFilter, searchTerm]);

    useEffect(() => {
        if (addModalOpen) {
            fetchSubCategories();
        }
    }, [addModalOpen]);

    useEffect(() => {
        if (addForm.subcategory_id) {
            fetchSubSubCategories(addForm.subcategory_id);
        } else {
            setSubSubCategories([]);
            setAddForm(prev => ({ ...prev, sub_sub_category_id: '' }));
        }
    }, [addForm.subcategory_id]);

    const fetchDoctors = async () => {
        try {
            setLoading(true);

            // بناء الـ URL مع الفلاتر
            let url = `/doctors/get_all`;
            const params = new URLSearchParams();
            
            // إضافة فلتر الحالة
            if (statusFilter === 'active') {
                params.append('status', '1');
            } else if (statusFilter === 'inactive') {
                params.append('status', '0');
            }
            
            // إضافة معامل البحث
            if (searchTerm.trim()) {
                params.append('search', searchTerm.trim());
            }
            
            // إضافة معامل الصفحة
            params.append('page', currentPage);
            
            // إضافة المعاملات للـ URL
            if (params.toString()) {
                url += `?${params.toString()}`;
            }

            
            const response = await apiClient.get(url);

            if (response.data.status === 'success' || response.data.statusCode === 200) {
                setDoctors(response.data.result?.data || []);
                setPagination(response.data.result?.pagination || null);
            } else {
                throw new Error(response.data.message || 'فشل في جلب الأطباء');
            }
        } catch (error) {
            showErrorToast(error.response?.data?.message || error.message || 'فشل في جلب الأطباء');
        } finally {
            setLoading(false);
        }
    };

    const handleStatusChange = async (doctorId, currentStatus) => {
        try {
            const newStatus = currentStatus === "1" ? "0" : "1";


            await apiClient.post(`/doctors/change_status/${doctorId}`, {
                status: newStatus
            });

            showSuccessToast('تم تغيير حالة الطبيب بنجاح');
            fetchDoctors(); // إعادة تحميل القائمة
        } catch (error) {
            showErrorToast('فشل في تغيير حالة الطبيب');
        }
    };

    const handleBanChange = async (doctorId, currentBanStatus) => {
        try {
            const newBanStatus = currentBanStatus === "1" ? "0" : "1";


            await apiClient.post(`/doctors/change_ban/${doctorId}?status=${newBanStatus}`, {});

            showSuccessToast('تم تغيير حالة الحظر بنجاح');
            fetchDoctors(); // إعادة تحميل القائمة
        } catch (error) {
            showErrorToast('فشل في تغيير حالة الحظر');
        }
    };


    const handleSearch = () => {
        setCurrentPage(1);
    };

    const handleClearFilters = () => {
        setSearchTerm('');
        setStatusFilter('active');
        setCurrentPage(1);
    };

    const getAuthToken = () => {
        const adminData = sessionStorage.getItem('adminkhadamat');
        if (adminData) {
            try {
                const parsed = JSON.parse(adminData);
                return parsed.data?.token?.access_token || parsed.data?.access_token;
            } catch {
                return null;
            }
        }
        return null;
    };

    const fetchSubCategories = async () => {
        try {
            setLoadingSubCategories(true);
            const token = getAuthToken();
            
            if (!token) {
                throw new Error("لا يوجد token للمصادقة");
            }

            const response = await axios.get(`${BASE_URL}/categories/subcategories/3`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Accept-Language": "ar",
                    lang: "ar",
                },
            });

            if (response.data.status === "success" || response.data.statusCode === 200) {
                setSubCategories(response.data.result || response.data.data || []);
            } else {
                throw new Error(response.data.message || "فشل في جلب الفئات الفرعية");
            }
        } catch (error) {
            showErrorToast(
                error.response?.data?.message || error.message || "فشل في جلب الفئات الفرعية"
            );
        } finally {
            setLoadingSubCategories(false);
        }
    };

    const fetchSubSubCategories = async (subCategoryId) => {
        try {
            setLoadingSubSubCategories(true);
            const token = getAuthToken();
            
            if (!token) {
                throw new Error("لا يوجد token للمصادقة");
            }

            const response = await axios.get(`${BASE_URL}/admin/sub_category/${subCategoryId}/sub_sub_categories`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Accept-Language": "ar",
                    lang: "ar",
                },
            });

            if (response.data.status === "success" || response.data.statusCode === 200) {
                setSubSubCategories(response.data.result || response.data.data || []);
            } else {
                throw new Error(response.data.message || "فشل في جلب الفئات الفرعية للفئات الفرعية");
            }
        } catch (error) {
            showErrorToast(
                error.response?.data?.message || error.message || "فشل في جلب الفئات الفرعية للفئات الفرعية"
            );
            setSubSubCategories([]);
        } finally {
            setLoadingSubSubCategories(false);
        }
    };

    const handleAddDoctor = async (e) => {
        e.preventDefault();
        
        if (!addForm.name || !addForm.phone || !addForm.password || !addForm.subcategory_id || !addForm.sub_sub_category_id || !addForm.gender) {
            showErrorToast("يرجى ملء جميع الحقول المطلوبة");
            return;
        }

        try {
            setAddingDoctor(true);
            const token = getAuthToken();
            
            if (!token) {
                showErrorToast("لا يوجد token للمصادقة");
                return;
            }

            // إضافة +20 قبل رقم الهاتف
            const phoneWithPrefix = `+20${addForm.phone}`;

            const response = await axios.post(
                `${BASE_URL}/doctor/regist`,
                {
                    name: addForm.name,
                    phone: phoneWithPrefix,
                    password: addForm.password,
                    description_ar: addForm.description_ar,
                    description_en: addForm.description_en,
                    subcategory_id: addForm.subcategory_id,
                    sub_sub_category_id: addForm.sub_sub_category_id,
                    gender: addForm.gender
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Accept-Language": "ar",
                        lang: "ar",
                        "Content-Type": "application/json",
                    },
                }
            );

            if (response.data.status === "success" || response.data.statusCode === 200) {
                showSuccessToast("تم إضافة الطبيب بنجاح");
                setAddModalOpen(false);
                setAddForm({ 
                    name: '', 
                    phone: '', 
                    password: '', 
                    description_ar: '', 
                    description_en: '', 
                    subcategory_id: '', 
                    sub_sub_category_id: '',
                    gender: ''
                });
                fetchDoctors(); // إعادة تحميل القائمة
            } else {
                throw new Error(response.data.message || "فشل في إضافة الطبيب");
            }
        } catch (error) {
            showErrorToast(
                error.response?.data?.message || error.message || "فشل في إضافة الطبيب"
            );
        } finally {
            setAddingDoctor(false);
        }
    };


    return (
        <div className="main_content admin-doctors-page">
            <div className="dashboard-container">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h4 className="mb-0">إدارة الأطباء</h4>
                                    <button 
                                        className="btn text-white" 
                                        style={{ backgroundColor: 'rgb(24, 65, 148)' }}
                        onClick={() => setAddModalOpen(true)}
                    >
                        <FaPlus className="mx-2" />
                        إضافة طبيب
                                </button>
                </div>


                {/* Doctors Table */}
                <div className="card shadow-lg border-0">
                    <div className="card-body">
                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-3 text-muted">جاري تحميل الأطباء...</p>
                            </div>
                        ) : !Array.isArray(doctors) || doctors.length === 0 ? (
                            <div className="text-center py-5">
                                <FaUserMd className="text-muted" style={{ fontSize: '3rem' }} />
                                <p className="mt-3 text-muted">لا يوجد أطباء</p>
                            </div>
                        ) : (
                            <>
                                <div className="table-responsive">
                                    <table className="table table-modern">
                                        <thead>
                                            <tr>
                                                <th className="text-center">#</th>
                                                <th className="text-center">اسم الطبيب</th>
                                                <th className="text-center">التخصص</th>
                                                <th className="text-center">الهاتف</th>
                                                <th className="text-center">التقييم</th>
                                                <th className="text-center">الحالة</th>
                                                <th className="text-center">الحظر</th>
                                                <th className="text-center">الإجراءات</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {doctors.map((doctor, index) => (
                                                <tr key={doctor.id} className="table-row-modern">
                                                    <td className="text-center">
                                                        <div className="order-number-badge">
                                                            #{(pagination?.current_page - 1) * (pagination?.per_page || 10) + index + 1}
                                                        </div>
                                                    </td>
                                                    <td className="text-center">
                                                        <div className="doctor-name">
                                                            {doctor.name || 'طبيب بدون اسم'}
                                                        </div>
                                                    </td>
                                                    <td className="text-center">
                                                        <div className="specialization-display">
                                                            {doctor.description || 'غير محدد'}
                                                        </div>
                                                    </td>
                                                    <td className="text-center">
                                                        <div className="phone-badge">
                                                            {doctor.phone ? (() => {
                                                                let phoneStr = doctor.phone.toString().trim();
                                                                phoneStr = phoneStr.replace(/\s+/g, '');
                                                                
                                                                // إذا كان الرقم يبدأ بـ + (مثل +201025376604) - نحوله إلى 201025376604+
                                                                if (phoneStr.startsWith('+')) {
                                                                    return phoneStr.slice(1) + '+';
                                                                }
                                                                
                                                                // إذا كان الرقم لا يبدأ بـ + - نضيف + في النهاية
                                                                return phoneStr + '+';
                                                            })() : 'غير محدد'}
                                                        </div>
                                                    </td>
                                                     <td className="text-center">
                                                         <div className="rating-display">
                                                             {doctor.rate_avg && doctor.rate_avg > 0 ? (
                                                                 <span>
                                                                     {doctor.rate_avg} ⭐ ({doctor.rate_count})
                                                                 </span>
                                                             ) : (
                                                                 <span className="text-muted">لا يوجد تقييم</span>
                                                             )}
                                                         </div>
                                                     </td>
                                                     <td className="text-center">
                                                         <span className={`badge ${doctor.status === "1" ? 'bg-success' : 'bg-danger'}`} style={{ color: 'white' }}>
                                                             {doctor.status === "1" ? 'نشط' : 'غير نشط'}
                                                         </span>
                                                     </td>
                                                     <td className="text-center">
                                                         <span className={`badge ${doctor.ban === "1" ? 'bg-danger' : 'bg-success'}`} style={{ color: 'white' }}>
                                                             {doctor.ban === "1" ? 'محظور' : 'غير محظور'}
                                                         </span>
                                                     </td>
                                                     <td className="text-center">
                                                        <div className="action-buttons">
                                                            {(hasPermission('doctors.reservations') || isSuperAdmin()) && (
                                                                <button
                                                                    className="btn btn-info btn-sm mx-2"
                                                                    onClick={() => navigate(`/doctors/doctor_reservations/${doctor.id}`)}
                                                                    title="حجوزات الطبيب">
                                                                    <FaCalendarAlt className="mx-1" />
                                                                    الحجوزات
                                                                </button>
                                                            )}
                                                            <motion.button
                                                                whileHover={{ scale: 1.1, y: -2 }}
                                                                whileTap={{ scale: 0.95 }}
                                                                className="btn btn-toggle btn-sm mx-2"
                                                                onClick={() => handleStatusChange(doctor.id, doctor.status)}
                                                                title={doctor.status === "1" ? 'إلغاء التنشيط' : 'تنشيط'}>
                                                                {doctor.status === "1" ? <FaToggleOff /> : <FaToggleOn />}
                                                            </motion.button>
                                                            <motion.button
                                                                whileHover={{ scale: 1.1, y: -2 }}
                                                                whileTap={{ scale: 0.95 }}
                                                                className={`btn btn-sm ${doctor.ban === "1" ? 'btn-unban' : 'btn-ban'}`}
                                                                onClick={() => handleBanChange(doctor.id, doctor.ban)}
                                                                title={doctor.ban === "1" ? 'إلغاء الحظر' : 'حظر'}>
                                                                {doctor.ban === "1" ? <FaCheck /> : <FaBan />}
                                                            </motion.button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Pagination */}
                                <div className="mt-4">
                                    <EnhancedPagination
                                        currentPage={pagination?.current_page || currentPage}
                                        totalPages={pagination?.pages_count || 1}
                                        onPageChange={setCurrentPage}
                                        totalItems={pagination?.total || 0}
                                        itemsPerPage={pagination?.per_page || 10}
                                        showInfo={true}
                                        className="mt-4"
                                    />
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Add Doctor Modal */}
            {addModalOpen && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999 }}>
                    <div className="modal-dialog modal-dialog-centered modal-lg" style={{ maxWidth: '700px' }}>
                        <div className="modal-content" style={{ borderRadius: '16px', overflow: 'hidden' }}>
                            <div className="modal-header" style={{ background: 'linear-gradient(135deg, #184194 0%, #1e40af 100%)', color: 'white' }}>
                                <h5 className="modal-title mb-0">إضافة طبيب جديد</h5>
                                <button 
                                    type="button" 
                                    className="btn-close btn-close-white" 
                                    onClick={() => {
                                        setAddModalOpen(false);
                                        setAddForm({ 
                                            name: '', 
                                            phone: '', 
                                            password: '', 
                                            description_ar: '', 
                                            description_en: '', 
                                            subcategory_id: '', 
                                            sub_sub_category_id: '',
                                            gender: ''
                                        });
                                    }}
                                ></button>
                            </div>
                            <form onSubmit={handleAddDoctor}>
                                <div className="modal-body">
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">اسم الطبيب</label>
                                            <input 
                                                type="text"
                                                className="form-control" 
                                                value={addForm.name} 
                                                onChange={(e) => setAddForm({ ...addForm, name: e.target.value })} 
                                                placeholder="أدخل اسم الطبيب"
                                                required 
                                            />
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">النوع</label>
                                            <select
                                                className="form-select"
                                                value={addForm.gender}
                                                onChange={e => setAddForm({ ...addForm, gender: e.target.value })}
                                                required
                                            >
                                                <option value="">اختر النوع</option>
                                                <option value="male">ذكر</option>
                                                <option value="female">أنثى</option>
                                            </select>
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">رقم الهاتف</label>
                                            <div className="input-group" style={{ direction: "ltr" }}>
                                                <span className="input-group-text" style={{ 
                                                    fontSize: '16px', 
                                                    fontWeight: '500',
                                                    background: '#f8f9fa',
                                                    color: '#6c757d',
                                                    border: '2px solid #e2e8f0'
                                                }}>
                                                    +20
                                                </span>
                                                <input 
                                                    type="tel"
                                                    className="form-control" 
                                                    value={addForm.phone} 
                                                    onChange={(e) => setAddForm({ ...addForm, phone: e.target.value })} 
                                                    placeholder="1xxxxx"
                                                    style={{
                                                        borderLeft: 'none',
                                                        borderRadius: '0 12px 12px 0',
                                                        border: '2px solid #e2e8f0',
                                                        padding: '12px 16px',
                                                        fontSize: '16px'
                                                    }}
                                                    required 
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">كلمة المرور</label>
                                            <input 
                                                type="password"
                                                className="form-control" 
                                                value={addForm.password} 
                                                onChange={(e) => setAddForm({ ...addForm, password: e.target.value })} 
                                                placeholder="أدخل كلمة المرور"
                                                required 
                                            />
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">التخصص</label>
                                            <select
                                                className="form-select"
                                                value={addForm.subcategory_id}
                                                onChange={(e) => setAddForm({ ...addForm, subcategory_id: e.target.value, sub_sub_category_id: '' })}
                                                required
                                                disabled={loadingSubCategories}
                                            >
                                                <option value="">اختر التخصص</option>
                                                {subCategories.map((subCat) => (
                                                    <option key={subCat.id} value={subCat.id}>
                                                        {subCat.title?.ar || subCat.name?.ar || subCat.title || subCat.name || `#${subCat.id}`}
                                                    </option>
                                                ))}
                                            </select>
                                            {loadingSubCategories && (
                                                <small className="text-muted">جاري التحميل...</small>
                                            )}
                                        </div>
                                        <div className="col-12 mb-3">
                                            <label className="form-label">التخصص الفرعي</label>
                                            <select
                                                className="form-select"
                                                value={addForm.sub_sub_category_id}
                                                onChange={(e) => setAddForm({ ...addForm, sub_sub_category_id: e.target.value })}
                                                required
                                                disabled={!addForm.subcategory_id || loadingSubSubCategories}
                                            >
                                                <option value="">
                                                    {!addForm.subcategory_id ? 'اختر التخصص أولاً' : 'اختر التخصص الفرعي'}
                                                </option>
                                                {subSubCategories.map((subSubCat) => (
                                                    <option key={subSubCat.id} value={subSubCat.id}>
                                                        {subSubCat.title?.ar || subSubCat.name?.ar || subSubCat.title || subSubCat.name || `#${subSubCat.id}`}
                                                    </option>
                                                ))}
                                            </select>
                                            {loadingSubSubCategories && (
                                                <small className="text-muted">جاري التحميل...</small>
                                            )}
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">الوصف بالعربية</label>
                                            <textarea
                                                className="form-control"
                                                rows="3"
                                                value={addForm.description_ar}
                                                onChange={(e) => setAddForm({ ...addForm, description_ar: e.target.value })}
                                                placeholder="أدخل الوصف بالعربية"
                                            />
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">الوصف بالإنجليزية</label>
                                            <textarea
                                                className="form-control"
                                                rows="3"
                                                value={addForm.description_en}
                                                onChange={(e) => setAddForm({ ...addForm, description_en: e.target.value })}
                                                placeholder="أدخل الوصف بالإنجليزية"
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer d-flex justify-content-center">
                                    <button 
                                        type="button" 
                                        className="btn btn-secondary mx-2" 
                                        onClick={() => {
                                            setAddModalOpen(false);
                                            setAddForm({ 
                                                name: '', 
                                                phone: '', 
                                                password: '', 
                                                description_ar: '', 
                                                description_en: '', 
                                                subcategory_id: '', 
                                                sub_sub_category_id: '',
                                                gender: ''
                                            });
                                        }}
                                        disabled={addingDoctor}
                                    >
                                        إلغاء
                                    </button>
                                    <button 
                                        type="submit" 
                                        className="btn text-white mx-2" 
                                        style={{ backgroundColor: '#184194' }}
                                        disabled={addingDoctor}
                                    >
                                        {addingDoctor ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                جاري الإضافة...
                                            </>
                                        ) : (
                                            'إضافة'
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminDoctors;

// CSS للأزرار
const style = document.createElement('style');
style.textContent = `
  .admin-doctors-page .table-modern td {
    vertical-align: middle;
  }

  .admin-doctors-page .specialization-display {
    display: block;
    max-width: 260px;
    margin: 0 auto;
    white-space: normal;
    word-break: break-word;
    color: #1f2937;
    line-height: 1.6;
  }

  .admin-doctors-page .phone-badge {
    background: #f0f9ff;
    color: #0369a1;
    padding: 0.45rem 0.9rem;
    border-radius: 10px;
    font-weight: 600;
    border: 1px solid #bae6fd;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    direction: ltr;
    min-width: 150px;
    max-width: 100%;
    white-space: nowrap;
    box-shadow: inset 0 0 0 1px rgba(191, 219, 254, 0.35);
  }

  .action-buttons {
    display: flex;
    gap: 0.5rem;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
  }
  
  .btn-info, .btn-toggle, .btn-ban, .btn-unban {
    white-space: nowrap !important;
    overflow: visible !important;
    text-overflow: unset !important;
    padding: 0.5rem 1rem !important;
    font-size: 0.875rem !important;
    font-weight: 500 !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
  }
  
  .btn-info {
    background: linear-gradient(135deg, #17a2b8 0%, #138496 100%) !important;
    color: white !important;
    font-size: 14px !important;
  }
  
  .btn-info span,
  .btn-toggle span {
    display: inline !important;
    visibility: visible !important;
    opacity: 1 !important;
  }
  
  /* Override any global styles that hide button text */
  .action-buttons .btn-info {
    font-size: 14px !important;
    padding: 0.5rem 1rem !important;
    width: auto !important;
    min-width: auto !important;
    max-width: none !important;
    height: auto !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
  }
  
  .action-buttons .btn-info * {
    display: inline !important;
    visibility: visible !important;
    opacity: 1 !important;
    font-size: inherit !important;
    line-height: normal !important;
    text-indent: 0 !important;
  }
  
  .btn-toggle {
    background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%) !important;
    color: white !important;
  }
  
  .btn-ban {
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%) !important;
    color: white !important;
  }
  
  .btn-unban {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%) !important;
    color: white !important;
  }
  
  .btn-info *, .btn-toggle *, .btn-ban *, .btn-unban * {
    color: white !important;
    visibility: visible !important;
    opacity: 1 !important;
  }
  
  .btn-toggle svg {
    margin-right: 0.25rem;
  }
`;
document.head.appendChild(style);
